import React from 'react';
import { Link } from 'react-router-dom';
import { businessMetrics, formatters } from '../../config/businessMetrics';
import { appConfig } from '../../config/appConfig';

const heroMetrics = [
  {
    value: businessMetrics.buyers.displayText,
    label: 'Qualified Buyers',
    description: 'Active and vetted across North America',
  },
  {
    value: businessMetrics.deals.displayText,
    label: 'Successful Deals',
    description: 'Founder-led engagements closed',
  },
  {
    value: businessMetrics.timeline.fullProcess.displayText,
    label: 'Average Timeline',
    description: 'Preparation to close with disciplined execution',
  },
  {
    value: businessMetrics.performance.higherValuations.displayText,
    label: 'Higher Valuations',
    description: 'Average uplift versus initial offers',
  },
];

const buyerHighlights = [
  {
    title: 'Strategic Buyers',
    value: businessMetrics.buyerTypes.strategic.displayText,
    description: 'Industry operators seeking synergies and expansion',
  },
  {
    title: 'Financial Buyers',
    value: businessMetrics.buyerTypes.financial.displayText,
    description: 'Private equity firms ready to deploy capital',
  },
  {
    title: 'Qualified Interest',
    value: businessMetrics.buyerTypes.total.displayText,
    description: 'Average engaged parties per process',
  },
];

const commitments = [
  {
    icon: (
      <svg className="w-8 h-8 text-amiva-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    ),
    title: 'Complete Confidentiality',
    description: `${businessMetrics.service.confidentiality} visibility and control at every step`,
  },
  {
    icon: (
      <svg className="w-8 h-8 text-amiva-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 6h4v12H4zm6-4h4v16h-4zm6 6h4v10h-4z"
        />
      </svg>
    ),
    title: 'Radical Transparency',
    description: `${businessMetrics.service.transparency} inside AMIVA+ so you always know the plan`,
  },
  {
    icon: (
      <svg className="w-8 h-8 text-amiva-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
    title: 'Hands-On Execution',
    description: 'Partner-level team driving prep, buyer outreach, and negotiations',
  },
  {
    icon: (
      <svg className="w-8 h-8 text-amiva-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: 'Aligned Incentives',
    description: 'Success-based pricing - no upfront fees, results only',
  },
];

const exploreLinks = [
  {
    title: 'Our Process',
    description:
      'A proven 12-week path from preparation to closing that keeps momentum and leverage.',
    cta: 'Learn more',
    to: '/process',
  },
  {
    title: 'Success Stories',
    description:
      'Founder outcomes across technology, healthcare, and service companies we have advised.',
    cta: 'View results',
    to: '/about/success-stories',
  },
  {
    title: 'Free Valuation',
    description:
      'Confidential valuation plus benchmarking insights so you know where you stand today.',
    cta: 'Start now',
    to: '/valuation',
  },
];

export default function AboutMaster() {
  return (
    <div className="bg-white">
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold mb-6">
                Boutique sell-side advisory for founders
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Building a better path to an exit with
                <span className="text-gradient block">competition, clarity, and speed</span>
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed mb-8 max-w-2xl font-medium">
                AMIVA pairs a senior M&A team with the AMIVA+ platform so you can watch every buyer touchpoint, control the narrative, and close on your timeline.
              </p>

              <ul className="space-y-3 mb-10">
                {[ 
                  'Structured buyer outreach engineered to maximize competition',
                  `${businessMetrics.team.experience.displayText} of collective deal experience on your side`,
                  `${formatters.currency(businessMetrics.deals.totalValue, true)} in completed and pipeline value handled by our team`,
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                      <svg className="w-3.5 h-3.5 text-emerald-600" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.333 8.667L7.111 10.444 10.667 6.222" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/valuation" className="btn-primary inline-flex items-center justify-center text-base sm:text-lg">
                  Get Your Valuation
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center text-base sm:text-lg"
                >
                  Talk with Our Team
                </a>
              </div>
            </div>

            <div>
              <div className="card-gradient p-10 rounded-3xl shadow-xl border border-gray-200">
                <div className="text-sm font-semibold uppercase tracking-wider text-amiva-primary mb-6">
                  Outcomes that define us
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {heroMetrics.map((metric) => (
                    <div key={metric.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1">
                      <div className="text-4xl font-bold text-gray-900 mb-2">{metric.value}</div>
                      <div className="text-sm font-semibold text-gray-700 mb-1">{metric.label}</div>
                      <p className="text-sm text-gray-500 leading-relaxed">{metric.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-sm text-gray-500">
                  Success fee only - Confidential by design - 24/7 access to your senior deal team
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold mb-4">
              The AMIVA advantage
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Measurable value through disciplined competition
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 font-medium">
              We blend institutional-grade process with founder empathy, creating momentum that traditional brokers cannot match.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-premium p-10 h-full flex flex-col">
              <span className="text-sm font-semibold text-amiva-primary uppercase tracking-wider mb-4">Valuation Growth</span>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Competition drives premium outcomes</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-6 font-medium">
                We position your story to create informed demand and managed tension across strategic and financial buyers.
              </p>
              <div className="flex items-end justify-center h-40 mb-6 bg-gray-50 rounded-lg p-6">
                <svg viewBox="0 0 240 120" className="w-full h-full">
                  {/* Grid lines for better readability */}
                  <defs>
                    <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                      <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="240" height="120" fill="url(#grid)" />
                  
                  {/* Main line with gradient */}
                  <defs>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                  <path d="M 20,100 Q 60,95 100,80 T 160,55 Q 200,40 220,30" fill="none" stroke="url(#lineGradient)" strokeWidth="5" strokeLinecap="round" />
                  
                  {/* Data points with better visibility */}
                  <circle cx="20" cy="100" r="5" fill="#3b82f6" stroke="white" strokeWidth="3" />
                  <circle cx="60" cy="95" r="5" fill="#3b82f6" stroke="white" strokeWidth="3" />
                  <circle cx="100" cy="80" r="5" fill="#3b82f6" stroke="white" strokeWidth="3" />
                  <circle cx="160" cy="55" r="5" fill="#3b82f6" stroke="white" strokeWidth="3" />
                  <circle cx="220" cy="30" r="6" fill="#8b5cf6" stroke="white" strokeWidth="3" />
                  
                  {/* Labels with better positioning and size */}
                  <text x="20" y="115" fill="#374151" fontSize="13" fontWeight="600" textAnchor="middle">Initial</text>
                  <text x="220" y="115" fill="#374151" fontSize="13" fontWeight="600" textAnchor="middle">Final</text>
                  
                  {/* Y-axis labels for better context */}
                  <text x="8" y="105" fill="#6b7280" fontSize="10" fontWeight="500" textAnchor="end">Low</text>
                  <text x="8" y="35" fill="#6b7280" fontSize="10" fontWeight="500" textAnchor="end">High</text>
                </svg>
              </div>
              <div className="pt-4 border-t border-gray-200 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Average valuation uplift</span>
                  <span className="font-bold text-gray-900 text-base">{businessMetrics.performance.higherValuations.displayText}</span>
                </div>
              </div>
            </div>

            <div className="card-premium p-10 h-full flex flex-col">
              <span className="text-sm font-semibold text-amiva-primary uppercase tracking-wider mb-4">Buyer Engagement</span>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Focused funnel built for action</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-6 font-medium">
                We map the universe, craft tailored outreach, and keep qualified buyers moving through diligence quickly.
              </p>
              <div className="space-y-5 mb-6">
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">2,500+ contacted</span>
                    <span className="text-xs font-medium text-gray-500">100%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-blue-600 to-blue-500 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">450 qualified</span>
                    <span className="text-xs font-medium text-gray-500">18%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-full" style={{ width: '70%' }}></div>
                  </div>
                </div>
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">85 LOIs</span>
                    <span className="text-xs font-medium text-gray-500">19%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-purple-600 to-purple-500 rounded-full" style={{ width: '40%' }}></div>
                  </div>
                </div>
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">12+ offers</span>
                    <span className="text-xs font-medium text-gray-500">14%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Conversion to LOI</span>
                  <span className="font-bold text-gray-900 text-base">18%</span>
                </div>
              </div>
            </div>

            <div className="card-premium p-10 h-full flex flex-col">
              <span className="text-sm font-semibold text-amiva-primary uppercase tracking-wider mb-4">Acceleration</span>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Optimized 12-week timeline</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-6 font-medium">
                Preparation, marketing, and closing phases run in parallel so diligence never stalls.
              </p>
              <div className="space-y-6 mb-6">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-base font-semibold text-gray-800">Preparation</span>
                    <span className="text-blue-600 font-bold text-sm">2 weeks</span>
                  </div>
                  <div className="h-5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-blue-600 to-blue-500 rounded-full flex items-center justify-end pr-2">
                      <span className="text-xs font-bold text-white">16%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-base font-semibold text-gray-800">Marketing</span>
                    <span className="text-indigo-600 font-bold text-sm">6-8 weeks</span>
                  </div>
                  <div className="h-5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-full flex items-center justify-end pr-2">
                      <span className="text-xs font-bold text-white">58%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-base font-semibold text-gray-800">Closing</span>
                    <span className="text-emerald-600 font-bold text-sm">2-4 weeks</span>
                  </div>
                  <div className="h-5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-full flex items-center justify-end pr-2">
                      <span className="text-xs font-bold text-white">26%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Total timeline</span>
                  <span className="font-bold text-gray-900 text-base">12 weeks</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">vs. traditional process</span>
                  <span className="font-bold text-emerald-600 text-base">60% faster</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-10 md:p-12 rounded-3xl shadow-xl border border-gray-200">
            <div className="text-sm font-semibold text-amiva-primary uppercase tracking-wider mb-4">From our founder</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why we built AMIVA</h2>
            <div className="space-y-5 text-lg text-gray-700 leading-relaxed font-medium">
              <p>
                Small business owners deserve the same institutional-quality M&A process as larger companies without the opacity and bloated timelines. We experienced the broken model first-hand and chose to rebuild it.
              </p>
              <p>
                Traditional brokers struggle to create competition. Big banks ignore deals under $500M. We bridge that gap with senior operators, transparent technology, and a process engineered for premium outcomes.
              </p>
              <p>
                Every mandate gets direct partner attention, real-time visibility through AMIVA+, and disciplined execution that keeps leverage on your side.
              </p>
            </div>
            <div className="mt-8">
              <div className="font-semibold text-gray-900">Michael A. Pantouvakis</div>
              <div className="text-sm text-gray-500">Founder & Managing Principal</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 h-[300px] w-[300px] rounded-full bg-blue-400/10 blur-3xl animate-pulse" style={{animationDuration: '8s'}} />
          <div className="absolute bottom-20 right-10 h-[300px] w-[300px] rounded-full bg-purple-400/10 blur-3xl animate-pulse" style={{animationDuration: '10s'}} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-blue-700 shadow-lg shadow-blue-500/10">
              <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              Who we serve
            </span>
            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-gray-900">
              Founder-led companies with <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">{businessMetrics.businessSize.revenueRange.displayText}</span> in revenue
            </h2>
            <p className="mt-4 text-lg text-gray-700 font-medium leading-relaxed">
              Our sweet spot spans recurring revenue technology, healthcare services, and essential service businesses across North America.
            </p>
          </div>

          {/* Industry Focus Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
            {businessMetrics.industries.primary.map((industry, index) => {
              const colors = [
                { bg: 'from-blue-500/10 via-blue-500/5 to-blue-500/10', border: 'border-blue-200/50', text: 'text-blue-700', icon: '💻' },
                { bg: 'from-purple-500/10 via-purple-500/5 to-purple-500/10', border: 'border-purple-200/50', text: 'text-purple-700', icon: '🏥' },
                { bg: 'from-indigo-500/10 via-indigo-500/5 to-indigo-500/10', border: 'border-indigo-200/50', text: 'text-indigo-700', icon: '⚙️' }
              ];
              const color = colors[index];
              
              return (
                <div key={industry} className="group relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br bg-white p-8 shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/20">
                  {/* Animated background gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${color.bg} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} />
                  
                  {/* Glow effect */}
                  <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br ${color.bg.replace('/10', '/20')} blur-3xl opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:-top-12 group-hover:-right-12`} />
                  
                  <div className="relative text-center">
                    <div className="text-4xl mb-4">{color.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-gray-800">
                      {industry}
                    </h3>
                  </div>

                  {/* Corner accent */}
                  <div className={`absolute bottom-0 right-0 h-28 w-28 translate-x-14 translate-y-14 rounded-full bg-gradient-to-tl ${color.bg.replace('/10', '/20')} blur-2xl opacity-0 transition-all duration-700 group-hover:opacity-60 group-hover:translate-x-10 group-hover:translate-y-10`} />
                </div>
              );
            })}
          </div>

          {/* Buyer Highlights Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {buyerHighlights.map((item, index) => {
              const colors = [
                { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', accent: 'bg-blue-100' },
                { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', accent: 'bg-purple-100' },
                { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', accent: 'bg-indigo-100' }
              ];
              const color = colors[index];
              
              return (
                <div key={item.title} className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative">
                    {/* Clean metric display */}
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${color.bg} ${color.border} ${color.text} border mb-4`}>
                      {item.value}
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom decorative element */}
          <div className="mt-16 flex justify-center">
            <div className="flex items-center gap-2">
              {buyerHighlights.map((_, index) => (
                <div 
                  key={index}
                  className="h-2 w-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse"
                  style={{
                    animationDelay: `${index * 200}ms`,
                    animationDuration: '2s'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white text-amiva-primary text-sm font-semibold mb-4 shadow-sm">
              Our commitments
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              The principles behind every engagement
            </h2>
            <p className="text-lg text-gray-700 font-medium">
              Outcomes matter, but how we achieve them matters more. These commitments guide every call, update, and negotiation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commitments.map((item) => (
              <div key={item.title} className="card-premium p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amiva-gradient-subtle flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-sm text-gray-700 leading-relaxed font-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-amiva-primary to-amiva-accent text-white p-10 md:p-16 shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
              <div className="max-w-xl">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  {businessMetrics.performance.higherValuations.displayText} higher valuations when competition is engineered in
                </h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  Our competitive process, transparent reporting, and senior-level execution keep leverage in your hands from the first conversation to the final signature.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Link
                  to="/valuation"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold shadow-lg hover:shadow-xl transition"
                >
                  Start Your Valuation
                </Link>
                <Link
                  to="/process"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/60 text-white font-semibold hover:bg-white/10 transition"
                >
                  Learn Our Process
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white text-amiva-primary text-sm font-semibold mb-4 shadow-sm">
              Explore next
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Go deeper into how we help founders exit on their terms
            </h2>
            <p className="text-lg text-gray-700 font-medium">
              Learn more about our approach, see the outcomes, or start your own valuation when you are ready.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {exploreLinks.map((link) => (
              <Link key={link.title} to={link.to} className="card-premium p-10 flex flex-col hover:-translate-y-1 transition-transform duration-300">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{link.title}</h3>
                <p className="text-gray-700 text-base leading-relaxed mb-6 font-medium">{link.description}</p>
                <span className="text-amiva-primary font-semibold inline-flex items-center mt-auto">
                  {link.cta}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
            Ready to maximize your exit value?
          </h2>
          <p className="text-lg text-gray-700 mb-8 font-medium">
            Join {businessMetrics.deals.displayText} founders who trusted AMIVA to run a competitive process and delivered premium outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/valuation" className="btn-primary inline-flex items-center justify-center text-base sm:text-lg">
              Get Started with Free Valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center text-base sm:text-lg"
            >
              Schedule a Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
